// 대균열(C12) 화면 모델. DOM 없이 표시 문자열·버튼 활성 판정·띠 자리를 순수 함수로 둔다(시험: griftBand.test.ts).
// 입구 선택 띠는 화면 아래 가장자리, 조이스틱(왼쪽 아래)과 스킬·공격 버튼(오른쪽) 사이 빈 자리에 놓는다 — 영웅·앞길 위는 가리지 않는다.
import { RiftPhase, type RiftView } from '../shared/field/view';
import { GRIFT_LIMIT_SEC } from '../shared/field/greaterRift';

export interface BandRect { x: number; y: number; w: number; h: number }
export interface BandInsets { left: number; top: number; right: number; bottom: number }

/** 남은 시간이 이 초 이하면 빨강(3분) */
export const GRIFT_WARN_SEC = 180;
/** 열쇠 최대 보유(표시용: 하루 2개, 최대 6개) */
export const GRIFT_KEY_MAX = 6;
/** 버튼 최소 크기(px) */
export const BAND_BTN_MIN = 44;
export const BAND_GAP = 6;
export const BAND_PAD = 6;
export const BAND_ROW_H = BAND_BTN_MIN;
/** 2줄 띠 최소 폭: − 단계 + · 안내 글(약 130px) · 도전이 한 줄에 읽히는 폭 */
export const BAND_WIDE_MIN_W = 360;
/** 3줄 띠 최소 폭: − 단계 + 도전(안내 글은 셋째 줄) = 44+52+44+56 + 간격 3 + 안쪽 여백 */
export const BAND_NARROW_MIN_W = 4 * 6 + 2 * 6 + 44 + 52 + 44 + 56 - 6;
/** 3줄 띠의 안내 글 줄 높이(두 줄 글) */
export const BAND_INFO_ROW_H = 30;

export type GriftRiftLike = Pick<RiftView, 'tier' | 'best' | 'cap' | 'selectable' | 'keys' | 'choice' | 'result' | 'newBest' | 'prevBest' | 'milestoneStones' | 'elapsedTicks' | 'limitTicks'>;

export function fmtClock(sec: number): string {
  const s = Math.max(0, Math.ceil(sec));
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}

export interface GriftClock { text: string; late: boolean; warn: boolean; sec: number }
/** 대균열 남은 시간(60Hz 틱). limitTicks가 0이면 규칙 상수(900초)를 쓴다 */
export function griftClock(limitTicks: number, elapsedTicks: number): GriftClock {
  const limit = limitTicks > 0 ? limitTicks : GRIFT_LIMIT_SEC * 60;
  const late = elapsedTicks > limit;
  const sec = Math.ceil(Math.max(0, limit - elapsedTicks) / 60);
  return { text: late ? '시간 초과' : fmtClock(sec), late, warn: late || sec <= GRIFT_WARN_SEC, sec: late ? 0 : sec };
}

/** 진행 중 균열 HUD 앞머리. 일반 균열(tier 0)은 null → 호출자가 기존 문구를 쓴다 */
export function griftHead(tier: number, gauge: number): string | null {
  return tier > 0 ? '대균열 ' + tier + '단계 · ' + Math.floor(gauge * 100) + '%' : null;
}

export function clampTier(t: number, selectable: number): number {
  if (!(selectable >= 1)) return 0;
  return Math.max(1, Math.min(Math.floor(selectable), Math.floor(Number.isFinite(t) ? t : selectable)));
}

export interface ChoiceModel {
  normalText: string; normalEnabled: boolean;
  tier: number; tierText: string; minusEnabled: boolean; plusEnabled: boolean;
  keysText: string; bestText: string; capText: string;
  challengeText: string; challengeEnabled: boolean;
  /** 열쇠 0 등 이유 안내(없으면 빈 문자열) */
  hint: string;
}
export function choiceModel(r: Pick<RiftView, 'best' | 'cap' | 'selectable' | 'keys'>, shards: number, shardsNeed: number, wantTier: number): ChoiceModel {
  const tier = clampTier(wantTier, r.selectable);
  const noKey = r.keys <= 0, noTier = r.selectable < 1 || r.cap <= 0;
  const ok = !noKey && !noTier;
  return {
    normalText: '일반 균열 · 조각 ' + Math.min(shards, shardsNeed) + '/' + shardsNeed,
    normalEnabled: shards >= shardsNeed,
    tier, tierText: tier > 0 ? tier + '단계' : '-',
    minusEnabled: tier > 1, plusEnabled: tier > 0 && tier < r.selectable,
    keysText: '열쇠 ' + r.keys + '/' + GRIFT_KEY_MAX,
    bestText: '최고 ' + (r.best > 0 ? r.best + '단계' : '기록 없음'),
    capText: '상한 ' + r.cap,
    challengeText: '도전',
    challengeEnabled: ok,
    hint: noKey ? '자정에 2개' : noTier ? '열 수 있는 단계 없음' : '',
  };
}

/** 위쪽 알림 띠 문구. result 0이면 null */
export function resultText(r: Pick<RiftView, 'tier' | 'result' | 'newBest' | 'milestoneStones'>): string | null {
  if (r.result === 1) {
    const tier = r.tier > 0 ? r.tier : r.newBest;
    return '대균열 ' + tier + '단계 돌파! 최고 기록 ' + r.newBest + (r.milestoneStones > 0 ? ' (+이정표 강화석 ' + r.milestoneStones + ')' : '');
  }
  if (r.result === 2) return '시간 초과 — 기록 없음';
  return null;
}

/**
 * 입구 선택 띠 자리(게임 화면 좌표). controls = 터치 조작 사각형(스킬·공격·물약 …), joy = 조이스틱 칸.
 * 오른쪽 조작(화면 가운데보다 오른쪽)의 왼쪽 끝과 조이스틱 사이 빈 자리를 쓴다. 가로 화면은 조이스틱 오른쪽·바닥 줄, 세로 폭이 좁으면 조이스틱 바로 위.
 * 두 줄: ① 일반 균열 · 닫기 ② − 단계 + · 열쇠/최고/상한 · 도전.
 */
export function bandLayout(w: number, h: number, joy: BandRect, controls: readonly BandRect[], safe: BandInsets): BandRect & { rows: number } {
  let rightMin = w - safe.right - 8;
  for (const c of controls) if (c.x + c.w / 2 > w / 2 && c.x - BAND_GAP < rightMin) rightMin = c.x - BAND_GAP;
  const landscape = w >= h;
  const left = (landscape ? joy.x + joy.w + BAND_GAP : safe.left + 8);
  const natural = Math.max(0, rightMin - left);
  // 넓으면 2줄, 좁으면 안내 글(열쇠·최고·상한)을 셋째 줄로 내려 3줄(글이 세로로 찌그러지지 않게).
  // 3줄도 안 들어가면(가운데에 스킬 바가 있는 키보드 화면 등) 2줄 최소 폭으로 넓히고 걸치는 조작 위로 올린다
  const lifted = natural < BAND_NARROW_MIN_W;
  const rows = natural >= BAND_WIDE_MIN_W || lifted ? 2 : 3;
  const width = lifted ? Math.max(0, Math.min(BAND_WIDE_MIN_W, w - safe.right - 8 - left)) : natural;
  const bh = bandHeight(rows);
  let bottom = landscape ? h - safe.bottom - 6 : joy.y - BAND_GAP;
  // 실제 터치 버튼(사용자 크기·지정 위치 반영)이 띠 자리에 걸리면 그 위로 올린다. 겹침 0이 기준이라 lifted가 아니어도 항상 확인한다
  for (let k = 0; k < 8; k++) {
    let moved = false;
    for (let i = 0; i <= controls.length; i++) {
      const c = i < controls.length ? controls[i] : landscape ? joy : null;
      if (!c || c.w < 1 || c.h < 1) continue;
      const xHit = left < c.x + c.w && left + width > c.x, yHit = bottom - bh < c.y + c.h && bottom > c.y;
      if (xHit && yHit) { bottom = c.y - BAND_GAP; moved = true; }
    }
    if (!moved) break;
  }
  return { x: left, y: Math.max(safe.top, bottom - bh), w: width, h: bh, rows };
}

/** 띠 높이: 버튼 줄 2개 + (3줄이면) 안내 글 줄 */
export function bandHeight(rows: number): number {
  return 2 * BAND_ROW_H + (rows >= 3 ? BAND_INFO_ROW_H + BAND_GAP : 0) + BAND_GAP + BAND_PAD * 2;
}

/** 균열 게이지(rift-hud) 크기·여백: 기본 폭 220, 좁은 자리용 압축 폭 156, 겹침 판정용 높이(머리글 줄 + 막대 + 여백, 배율 전) */
export const RIFT_HUD_W = 220;
export const RIFT_HUD_W_COMPACT = 156;
export const RIFT_HUD_H = 48;
export const RIFT_HUD_GAP = 6;

export interface RiftHudSlot { right: number; width: number; top: number; moved: boolean; compact: boolean }

/**
 * 오른쪽 기둥(오른쪽 8px·위 118px) 패널 자리. 기본 자리가 터치 조작(각성 게이지·스킬 호·물약 …)과 겹치면
 * ① 겹치는 조작 왼쪽으로 밀고 ② 그래도 화면 가운데(보스·대상 체력 막대 자리)를 넘으면 다음(더 좁은) 폭으로 밀어 낸다.
 * 반환 right/width/top은 배율 전 CSS px(배율은 오른쪽 위 모서리 기준). 어느 후보도 안 되면 기본 자리를 돌려준다.
 */
export function columnSlot(w: number, scale: number, controls: readonly BandRect[], safe: BandInsets, top: number, widths: readonly number[], height: number): RiftHudSlot {
  const base = safe.right + 8;
  const hit = (right: number, width: number): BandRect[] => {
    const x1 = w - right, x0 = x1 - width * scale, y1 = top + height * scale;
    return controls.filter((c) => c.x < x1 + RIFT_HUD_GAP && c.x + c.w > x0 - RIFT_HUD_GAP && c.y < y1 + RIFT_HUD_GAP && c.y + c.h > top - RIFT_HUD_GAP);
  };
  for (const width of widths) {
    let right = base;
    for (let i = 0; i <= controls.length; i++) {
      const hs = hit(right, width);
      if (hs.length === 0) break;
      right = w - Math.min(...hs.map((c) => c.x)) + RIFT_HUD_GAP;
    }
    const left = w - right - width * scale;
    if (hit(right, width).length === 0 && left >= w / 2 + 8) return { right, width, top, moved: right !== base, compact: width !== widths[0] };
  }
  return { right: base, width: widths[0], top, moved: false, compact: false };
}

/** 대균열 진행 시계(rift-hud) 자리: 기본 폭 220, 좁은 자리용 압축 폭 156 */
export function riftHudSlot(w: number, scale: number, controls: readonly BandRect[], safe: BandInsets, top = 118): RiftHudSlot {
  return columnSlot(w, scale, controls, safe, top, [RIFT_HUD_W, RIFT_HUD_W_COMPACT], RIFT_HUD_H);
}

/**
 * 균열 게이지 세로 자리: 기본 top에서 시작해 x 범위가 겹치는 위쪽 HUD(obstacles, 게임 화면 좌표) 바로 아래로 내린다.
 * 장애물 아래쪽 끝 + 간격이 새 top. 겹침이 없으면 top 그대로.
 */
export function riftHudTop(top: number, x0: number, x1: number, height: number, obstacles: readonly BandRect[]): number {
  let t = top;
  for (let k = 0; k <= obstacles.length; k++) {
    const hit = obstacles.find((o) => o.w > 0 && o.h > 0 && o.x < x1 && o.x + o.w > x0 && o.y < t + height && o.y + o.h > t);
    if (!hit) break;
    t = hit.y + hit.h + RIFT_HUD_GAP;
  }
  return t;
}

/** 위쪽 알림 띠(field-band) 높이: 한 줄 36, 두 줄 48 / 최대 폭 520 / 위 여백 4 */
export const TOP_BAND_H1 = 36;
export const TOP_BAND_H2 = 48;
export const TOP_BAND_MAX_W = 520;
export const TOP_BAND_TOP = 4;
export const TOP_BAND_MIN_W = 120;
export interface TopBandSlot { x: number; w: number; wrap: boolean }

/**
 * 위쪽 알림 띠 자리. 위 줄에 있는 다른 HUD(왼쪽 위 영웅 패널·오른쪽 위 미니맵·메뉴 …)를 비킨 빈 구간 중 화면 가운데를 품은 것(없으면 가장 넓은 것)에
 * 글자가 한 줄로 들어가면(textW = 한 줄 자연 폭, 안쪽 여백 포함) 그 폭으로 가운데 정렬, 모자라면 구간 폭으로 두 줄 접는다.
 * obstacles = 게임 화면 좌표. 띠의 세로 범위(두 줄 높이)와 겹치는 것만 본다.
 */
export function topBandSlot(w: number, textW: number, obstacles: readonly BandRect[]): TopBandSlot {
  const y0 = TOP_BAND_TOP, y1 = TOP_BAND_TOP + TOP_BAND_H2;
  let spans: Array<[number, number]> = [[8, Math.max(8, w - 8)]];
  for (const o of obstacles) {
    if (!(o.w > 0 && o.h > 0) || o.y >= y1 || o.y + o.h <= y0) continue;
    const a = o.x - BAND_GAP, b = o.x + o.w + BAND_GAP;
    const next: Array<[number, number]> = [];
    for (const [lo, hi] of spans) {
      if (b <= lo || a >= hi) { next.push([lo, hi]); continue; }
      if (a > lo) next.push([lo, a]);
      if (b < hi) next.push([b, hi]);
    }
    spans = next;
  }
  let best = spans.find(([lo, hi]) => lo <= w / 2 && w / 2 <= hi) ?? spans.reduce<[number, number] | null>((m, s) => (!m || s[1] - s[0] > m[1] - m[0] ? s : m), null);
  if (!best) best = [8, Math.max(8, w - 8)];
  const cap = Math.max(Math.min(TOP_BAND_MAX_W, best[1] - best[0]), Math.min(TOP_BAND_MIN_W, best[1] - best[0]));
  const wrap = textW > cap;
  const bw = wrap ? cap : Math.ceil(textW);
  const x = Math.round(Math.max(best[0], Math.min(best[1] - bw, w / 2 - bw / 2)));
  return { x, w: bw, wrap };
}

/**
 * 균열 HUD 자리 캐시(프레임 루프용, 할당 없음): 화면 크기·배율·장애물 아래 끝이 바뀌었는지 숫자로만 비교하고,
 * 바뀌었으면 key를 새 값으로 고치고 true. key = [W, H, u×1000, (x, 아래끝) × 장애물 수] 길이 3 + 2n의 미리 만든 배열.
 */
export function riftLayoutChanged(key: Float64Array, w: number, h: number, u: number, obs: readonly BandRect[]): boolean {
  // 비캡처 헬퍼(setKey)만 쓴다: 호출마다 클로저를 만들지 않는다. `|`로 이어 모든 칸을 갱신한다
  let c = setKey(key, 0, w) | setKey(key, 1, h) | setKey(key, 2, Math.round(u * 1000));
  for (let i = 0; i < obs.length; i++) {
    const o = obs[i], seen = o.w > 0 && o.h > 0;
    c |= setKey(key, 3 + i * 2, seen ? Math.round(o.x) : 0);
    c |= setKey(key, 4 + i * 2, seen ? Math.round(o.y + o.h) : 0);
  }
  return c !== 0;
}
/** key[i]를 v로 고치고 바뀌었으면 1, 같으면 0 (모듈 바깥 함수라 할당 없음) */
export function setKey(key: Float64Array, i: number, v: number): number {
  if (key[i] === v) return 0;
  key[i] = v;
  return 1;
}

/** 균열 알림 띠 신호: 0 없음 · 1 수호자 등장(Guardian으로 바뀐 그 한 번) · 2 다음 층 통로 열림(막 열린 그 한 번) */
export function riftBandCue(prevPhase: number, phase: number, prevPortal: boolean, portalActive: boolean): 0 | 1 | 2 {
  if (phase === RiftPhase.Guardian && prevPhase !== RiftPhase.Guardian) return 1;
  if (portalActive && !prevPortal && phase === RiftPhase.Hunt) return 2;
  return 0;
}

/**
 * 입구 선택 띠 입력 캐시(프레임 루프용, 할당 없음): 원시 입력(최고·상한·선택 가능·열쇠·조각·고른 단계)이 숫자로 하나라도 바뀌었을 때만 true.
 * true일 때만 choiceModel()과 표시 문자열을 다시 만든다. key = 길이 6 이상의 미리 만든 배열.
 */
export function choiceInputsChanged(key: Float64Array, r: Pick<RiftView, 'best' | 'cap' | 'selectable' | 'keys'>, shards: number, wantTier: number): boolean {
  const c = setKey(key, 0, r.best) | setKey(key, 1, r.cap) | setKey(key, 2, r.selectable) | setKey(key, 3, r.keys) | setKey(key, 4, shards) | setKey(key, 5, wantTier);
  return c !== 0;
}
